import { TbFlag, TbFlagFilled } from "react-icons/tb";
import axios from "axios";
import { getApiBaseUrl } from "../apiBaseUrlFinder";
import { useEngagementCache } from "../hooks/useEngagementCache";
import {
  AiFillDislike,
  AiFillLike,
  AiOutlineDislike,
  AiOutlineLike,
} from "react-icons/ai";

const ManagerReviewSection = ({ managerData, setManagerData }) => {
  const { getEngagement, updateEngagement } = useEngagementCache();

  const handleEngagement = async (reviewId, type) => {
    // Get current engagement state
    const currentEngagement = getEngagement(reviewId);

    // Prevent reporting more than once
    if (type === "report" && currentEngagement.reported) {
      return;
    }

    // Prevent re-clicking the same action or switching between like/dislike
    if ((type === "like" && (currentEngagement.like || currentEngagement.dislike)) || 
        (type === "dislike" && (currentEngagement.dislike || currentEngagement.like))) {
      return;
    }

    // Calculate the new engagement state
    let newEngagement = { ...currentEngagement };

    if (type === "like") {
      newEngagement.like = !currentEngagement.like;
      if (newEngagement.like) {
        newEngagement.dislike = false;
      }
    } else if (type === "dislike") {
      newEngagement.dislike = !currentEngagement.dislike;
      if (newEngagement.dislike) {
        newEngagement.like = false;
      }
    } else if (type === "report") {
      // One-time report; do not alter like/dislike
      newEngagement.reported = true;
    }

    // Update engagement in cache (this handles localStorage automatically)
    updateEngagement(reviewId, type);

    // // Optimistically update the counts inside managerData
    // const updatedReviews = managerData.reviews.map((review) => {
    //   if (review.review_id !== reviewId) return review;

    //   let updatedLikes = review.likes;
    //   let updatedDislikes = review.dislikes;

    //   if (type === "like") {
    //     if (currentEngagement.like) {
    //       updatedLikes -= 1;
    //     } else {
    //       updatedLikes += 1;
    //       if (currentEngagement.dislike) {
    //         updatedDislikes -= 1;
    //       }
    //     }
    //   } else if (type === "dislike") {
    //     if (currentEngagement.dislike) {
    //       updatedDislikes -= 1;
    //     } else {
    //       updatedDislikes += 1;
    //       if (currentEngagement.like) {
    //         updatedLikes -= 1;
    //       }
    //     }
    //   }

    //   return {
    //     ...review,
    //     likes: updatedLikes,
    //     dislikes: updatedDislikes,
    //   };
    // });

    // // Now update the local managerData state
    // if (setManagerData) {
    //   setManagerData(prevData => ({
    //     ...prevData,
    //     reviews: updatedReviews
    //   }));
    // }

    // Make API call for both logged-in and anonymous users
    const isReport = type === "report";
    const payload = {
      manager_id: managerData.manager_id,
      manager_full_name: managerData.manager_full_name,
      review_id: reviewId,
      like: isReport ? false : newEngagement.like,
      dislike: isReport ? false : newEngagement.dislike,
      reported: isReport ? true : false,
    };

    try {
      const { data } = await axios.put(`${getApiBaseUrl()}/review_engagement`, payload);

      // If server returns canonical counts, update this review's counts
      const serverProfile = data?.review_profile;
      if (serverProfile && setManagerData) {
        setManagerData(prevData => ({
          ...prevData,
          reviews: prevData.reviews.map(r =>
            r.review_id === reviewId
              ? { ...r, likes: serverProfile.likes, dislikes: serverProfile.dislikes }
              : r
          )
        }));
      }
    } catch (error) {
      console.error("Error updating engagement:", error);
      // Optionally: Rollback local state here
    }
  };

  return (
    <>
      {managerData?.reviews?.map((review) => {
        return (
          <div
            key={review?.review_id}
            className="manager_profile_review_box my-lg-4 my-md-4 my-3"
          >
            <div className="py-3 px-lg-4 px-md-3 px-3">
              <div className="row">
                <div className="col-lg-2 col-md-2 col-12">
                  <div>
                    <div className="d-flex">
                      <div>
                        <h5 className="rating_manager_profile_h5">Rating</h5>
                        <div
                          className={`rating_profile_box ${
                            review?.review_rating < 2.5
                              ? "rating_profile_box_red"
                              : ""
                          }`}
                        >
                          {review?.review_rating}
                        </div>
                      </div>
                      <div className="border_right_manager_rating ms-4"></div>
                    </div>
                    <div className="border_bottom_manager_rating mt-4"></div>
                  </div>
                </div>
                <div className="col-lg-10 col-md-10 col-12">
                  <div className="d-flex align-items-center justify-content-between pt-lg-0 pt-md-0 pt-3">
                    <div>
                      <h5 className="mb-0 tags_manager_profile">Tags</h5>
                    </div>
                    <div className="d-flex align-items-center">
                      {review?.is_verified ? (
                        <div className="verified-tooltip me-2">
                          <span className="verified-badge">Verified</span>
                          <span className="tooltip-text">
                            Verified reviews are submitted by individuals who
                            have confirmed their identity and employment history
                            with this manager.
                          </span>
                        </div>
                      ) : (
                        <div className="unverified-tooltip me-2">
                          <span className="unverified-badge">Unverified</span>
                          <span className="tooltip-text">
                            This review is by an unverified user and will not
                            count towards the manager's official rating.
                          </span>
                        </div>
                      )}
                      <p className="mb-0 tags_manager_profile_date">
                        {review?.creation_time}
                      </p>
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    <div>
                      <div className="d-flex flex-wrap gap-2 pt-2">
                        {review?.positive_tags?.map((tag) => (
                          <div
                            key={tag}
                            className="px-3 py-1"
                            style={{
                              backgroundColor: "#EDFDED",
                              color: "#2C2C2C",
                              cursor: "pointer",
                              borderRadius: "30px",
                              fontSize: "0.85em",
                              height: "28px",
                            }}
                          >
                            {tag}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="border_difference_tags ms-2 mt-2"></div>
                    <div>
                      <div className="d-flex flex-wrap gap-2 pt-2 ps-2">
                        {review?.negative_tags?.map((tag) => (
                          <div
                            key={tag}
                            className="px-3 py-1"
                            style={{
                              backgroundColor: "#FFD0CA",
                              color: "#2C2C2C",
                              cursor: "pointer",
                              borderRadius: "30px",
                              fontSize: "0.85em",
                              height: "28px",
                            }}
                          >
                            {tag}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="manager_reviews_box p-3 mt-lg-4 mt-md-3 mt-3">
                    <p className="manager_reviews_box_p">{review?.review}</p>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="d-flex justify-content-between pt-2">
                  <div>
                    {getEngagement(review.review_id).like ? (
                      <AiFillLike
                        fontSize={25}
                        color="#151515"
                        onClick={() =>
                          handleEngagement(review.review_id, "like")
                        }
                        style={{ cursor: (getEngagement(review.review_id).like || getEngagement(review.review_id).dislike) ? "not-allowed" : "pointer" }}
                      />
                    ) : (
                      <AiOutlineLike
                        fontSize={25}
                        color="#151515"
                        onClick={() =>
                          handleEngagement(review.review_id, "like")
                        }
                        style={{ cursor: (getEngagement(review.review_id).like || getEngagement(review.review_id).dislike) ? "not-allowed" : "pointer" }}
                      />
                    )}
                    <span className="add_review_p mx-1 fw-bold">
                      {review?.likes}
                    </span>

                    {getEngagement(review.review_id).dislike ? (
                      <AiFillDislike
                        fontSize={25}
                        color="#151515"
                        className="ms-2"
                        onClick={() =>
                          handleEngagement(review.review_id, "dislike")
                        }
                        style={{ cursor: (getEngagement(review.review_id).dislike || getEngagement(review.review_id).like) ? "not-allowed" : "pointer" }}
                      />
                    ) : (
                      <AiOutlineDislike
                        fontSize={25}
                        color="#151515"
                        className="ms-2"
                        onClick={() =>
                          handleEngagement(review.review_id, "dislike")
                        }
                        style={{ cursor: (getEngagement(review.review_id).dislike || getEngagement(review.review_id).like) ? "not-allowed" : "pointer" }}
                      />
                    )}
                    <span className="add_review_p mx-1 fw-bold">
                      {review?.dislikes}
                    </span>
                  </div>
                  <div>
                    {getEngagement(review.review_id).reported ? (
                      <TbFlagFilled
                        fontSize={25}
                        color="#151515"
                        onClick={() => {}}
                        style={{ cursor: "not-allowed" }}
                      />
                    ) : (
                      <TbFlag
                        fontSize={25}
                        color="#151515"
                        onClick={() =>
                          handleEngagement(review.review_id, "report")
                        }
                        style={{ cursor: "pointer" }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default ManagerReviewSection;
